import { Invitation } from 'sip.js';
import { downloadGreetingBlob } from '@/services/files/greetingFileApi';
const MAX_TRACK_WAIT_MS = 3000;
const TRACK_POLL_MS = 50;
let preloadedBuffer = null;
let userPreloadPromise = null;
let preloadedForUploadedBy = null;
let lastUploadedByEmail = null;
const greetedSessionIds = new Set();
const activeSessions = new Map();
export function resetCallGreetingCache() {
    preloadedBuffer = null;
    userPreloadPromise = null;
    preloadedForUploadedBy = null;
    lastUploadedByEmail = null;
}
/** Clear cache and fetch the latest greeting (after upload or delete). */
export function reloadUserCallGreeting(uploadedBy) {
    resetCallGreetingCache();
    return preloadUserCallGreeting(uploadedBy);
}
async function decodeAudioBlob(blob) {
    const arrayBuffer = await blob.arrayBuffer();
    const decodeContext = new AudioContext();
    try {
        return await decodeContext.decodeAudioData(arrayBuffer);
    }
    finally {
        await decodeContext.close();
    }
}
/**
 * Download the agent's greeting from CRM files API (`category=grading`).
 * No local/default fallback — returns null when no file exists.
 */
export function preloadUserCallGreeting(uploadedBy) {
    const email = uploadedBy.trim();
    if (!email) {
        return Promise.resolve(null);
    }
    if (preloadedBuffer && preloadedForUploadedBy === email) {
        return Promise.resolve(preloadedBuffer);
    }
    if (userPreloadPromise) {
        return userPreloadPromise;
    }
    preloadedBuffer = null;
    preloadedForUploadedBy = email;
    lastUploadedByEmail = email;
    userPreloadPromise = (async () => {
        try {
            const blob = await downloadGreetingBlob(email);
            if (blob) {
                preloadedBuffer = await decodeAudioBlob(blob);
                console.info('[call-greeting] Preloaded user greeting for', email);
                return preloadedBuffer;
            }
            console.info('[call-greeting] No uploaded greeting for', email);
            preloadedBuffer = null;
            preloadedForUploadedBy = null;
            return null;
        }
        catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('[call-greeting] Failed to preload user greeting for', email, message);
            preloadedBuffer = null;
            preloadedForUploadedBy = null;
            return null;
        }
        finally {
            userPreloadPromise = null;
        }
    })();
    return userPreloadPromise;
}
/**
 * Play the agent's uploaded greeting once when an **inbound** call is answered.
 * Skips outbound dials (including supervisor snoop/whisper/barge/conference codes).
 */
export function playInboundCallGreeting(session) {
    if (!(session instanceof Invitation)) {
        return;
    }
    if (greetedSessionIds.has(session.id)) {
        return;
    }
    void (async () => {
        let buffer = preloadedBuffer;
        if (!buffer && lastUploadedByEmail) {
            buffer = await preloadUserCallGreeting(lastUploadedByEmail);
        }
        if (!buffer) {
            return;
        }
        greetedSessionIds.add(session.id);
        const player = new CallGreetingSession(session, buffer);
        activeSessions.set(session.id, player);
        await player.start();
        if (!player.isActive()) {
            activeSessions.delete(session.id);
        }
    })();
}
/** Stop greeting playback and release Web Audio resources when the call ends. */
export function cleanupCallGreeting(session) {
    greetedSessionIds.delete(session.id);
    const player = activeSessions.get(session.id);
    if (player) {
        player.stop();
        activeSessions.delete(session.id);
    }
}
async function waitForAudioSender(session) {
    const deadline = Date.now() + MAX_TRACK_WAIT_MS;
    while (Date.now() < deadline) {
        const sdh = session.sessionDescriptionHandler;
        const pc = sdh?.peerConnection;
        const micTrack = sdh?.localMediaStream?.getAudioTracks()[0];
        const sender = pc?.getSenders().find((s) => s.track?.kind === 'audio');
        if (sender && micTrack && micTrack.readyState === 'live') {
            return { sender, micTrack };
        }
        await new Promise((resolve) => window.setTimeout(resolve, TRACK_POLL_MS));
    }
    console.error('[call-greeting] Timed out waiting for live microphone track on session', session.id);
    return null;
}
class CallGreetingSession {
    session;
    buffer;
    stopped = false;
    active = false;
    audioContext = null;
    greetingSource = null;
    mixTrack = null;
    originalMicTrack = null;
    sender = null;
    constructor(session, buffer) {
        this.session = session;
        this.buffer = buffer;
    }
    isActive() {
        return this.active;
    }
    async start() {
        const tracks = await waitForAudioSender(this.session);
        if (!tracks || this.stopped) {
            return;
        }
        this.sender = tracks.sender;
        this.originalMicTrack = tracks.micTrack;
        try {
            const ctx = new AudioContext();
            this.audioContext = ctx;
            if (ctx.state === 'suspended') {
                await ctx.resume();
            }
            const destination = ctx.createMediaStreamDestination();
            this.mixTrack = destination.stream.getAudioTracks()[0];
            const micSource = ctx.createMediaStreamSource(new MediaStream([tracks.micTrack]));
            const micGain = ctx.createGain();
            micGain.gain.value = 0;
            micSource.connect(micGain);
            micGain.connect(destination);
            const greetingSource = ctx.createBufferSource();
            greetingSource.buffer = this.buffer;
            this.greetingSource = greetingSource;
            greetingSource.connect(destination);
            greetingSource.connect(ctx.destination);
            await this.sender.replaceTrack(this.mixTrack);
            this.active = true;
            greetingSource.addEventListener('ended', () => this.finish('completed'), { once: true });
            greetingSource.start(0);
            console.info('[call-greeting] Playing inbound greeting on session', this.session.id);
        }
        catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('[call-greeting] Failed to inject greeting on session', this.session.id, message);
            this.finish('error');
        }
    }
    stop() {
        this.finish('stopped');
    }
    finish(reason) {
        if (this.stopped) {
            return;
        }
        this.stopped = true;
        this.active = false;
        try {
            this.greetingSource?.stop();
        }
        catch {
            /* already stopped */
        }
        if (this.sender && this.originalMicTrack?.readyState === 'live') {
            void this.sender.replaceTrack(this.originalMicTrack).catch((error) => {
                const message = error instanceof Error ? error.message : String(error);
                console.error('[call-greeting] Failed to restore microphone track:', message);
            });
        }
        this.mixTrack?.stop();
        void this.audioContext?.close();
        this.audioContext = null;
        this.greetingSource = null;
        this.mixTrack = null;
        console.info(`[call-greeting] Cleanup (${reason}) session`, this.session.id);
    }
}
